import { useDndMonitor, useDraggable } from "@dnd-kit/core";
import Image from "next/image";
import { useCallback, useMemo, useState } from "react";
import { BsEyeSlashFill } from "react-icons/bs";
import { GiCometSpark, GiCrossedSwords } from "react-icons/gi";
import { IoStar, IoStarOutline } from "react-icons/io5";

import { CardCategoryEnum, DeckVisibilityEnum } from "@sorcery/db";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  InternalLink,
  cn,
} from "@sorcery/ui";
import { fromNow, isNew } from "@sorcery/utils/universal/time";

import { toast } from "@sorcery/ui";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { MdOutlineMoreHoriz } from "react-icons/md";
import { RxDragHandleDots2 } from "react-icons/rx";
import { ModalTypeEnum, useModal } from "~/providers/ModalProvider";
import { useUser } from "~/providers/UserProvider";
import { LayoutEnum } from "~/state/configJotai";
import { foldersJotai } from "~/state/deckJotai";
import type { DeckPreviewType } from "~/types/deck";
import { SessionStorageKeys } from "~/types/storage";
import { hasPrimer, isPrivate } from "~/utils/deck";
import { deckPath } from "~/utils/paths";
import { api } from "~/utils/trpc";
import { Metadata } from "../Metadata";
import { DeckThreshholds } from "./DeckThreshholds";

type Props = {
  deck: DeckPreviewType;
  layout: LayoutEnum;
};

export const MyDeckPreview = ({ deck, layout }: Props) => {
  const rotate = deck.feature.card.guardian?.category === CardCategoryEnum.Site;
  const { user } = useUser();
  const folders = useAtomValue(foldersJotai);
  const { query: routerParams } = useRouter();
  const folderId = routerParams.folder as string | undefined;
  const trpcUtils = api.useUtils();
  const [dndActive, setDndActive] = useState(false);
  useDndMonitor({
    onDragStart: () => setDndActive(true),
    onDragEnd: () => setDndActive(false),
  });
  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id: deck.id,
  });

  const { mutate: toggleStar, isPending: isStarring } = api.deck.toggleStar.useMutation({
    onSuccess: () => {
      toast(`Deck ${!starred ? "starred" : "unstarred"}`);
      void trpcUtils.user.getBySession.invalidate();
      void trpcUtils.deck.getByUserId.invalidate();
    },
    onError: () => {
      toast.error("Something went wrong");
    },
  });

  const { mutate: deleteDeck } = api.deck.delete.useMutation({
    onSuccess: () => {
      toast("Deck deleted");
      void trpcUtils.user.getBySession.invalidate();
      void trpcUtils.deck.getByUserId.invalidate();
    },
    onError: () => {
      toast.error("Something went wrong");
    },
  });

  const { mutate: updateSettings } = api.deck.updateSettings.useMutation({
    onSuccess: () => {
      toast("Visibility updated");
      void trpcUtils.deck.getByUserId.invalidate();
    },
    onError: () => {
      toast.error("Something went wrong");
    },
  });

  const { openModal } = useModal();

  const starred = useMemo(
    () => !!user?.stars.find((f) => f.deckId === deck.id),
    [deck.id, user?.stars],
  );

  const transformRotate = useMemo(() => {
    return rotate ? "scale-[0.715] rotate-[90deg] translate-y-[-25%] " : "translate-y-[-17%]";
  }, [rotate]);

  const transformScale = useMemo(() => {
    return rotate ? "group-hover:scale-[0.8]" : "group-hover:scale-[1.05]";
  }, [rotate]);

  const onDelete = useCallback(async () => {
    // biome-ignore lint/style/noRestrictedGlobals: <explanation>
    if (sessionStorage.getItem(SessionStorageKeys.DONTASKDELETE)) {
      void deleteDeck({ deckId: deck.id });
    } else {
      openModal(ModalTypeEnum.DELETEDECK, { deckId: deck.id });
    }
  }, [deck, deleteDeck, openModal]);

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      // opacity-100 override is needed to counteract dnd-kits default behaviour of toggling the
      // opacity to 0 on drag end for a brief moment to simulate it disappearing,
      // which even happens on an unsuccessful drop event
      className={cn(
        "relative group animate-in fade-in !opacity-100",
        layout === LayoutEnum.LIST &&
          "flex pb-2 justify-between items-center border-b border-b-secondary",
        dndActive && !isDragging && "!opacity-30 !pointer-events-none",
      )}
      style={style}
    >
      <InternalLink
        key={deck.id + layout}
        href={deckPath(deck.id)}
        className={cn(
          "flex flex-col rounded-xl border border-transparent transition",
          layout === LayoutEnum.GRID &&
            "hover:border-white hover:shadow-[0_0_14px_rgba(255,255,255,0.75)]",
          layout === LayoutEnum.LIST && "grow hover:opacity-50 hover:scale-[102%]",
        )}
      >
        {layout === LayoutEnum.GRID ? (
          <div
            className="relative h-[150px] w-full rounded-xl"
            style={{
              backgroundImage:
                "linear-gradient(to top, rgb(2, 2, 2, 0.8) 0%, rgba(2, 2, 2, 0.6) 15%, transparent 40%)",
            }}
          >
            {/* Backdrop */}
            <div className="absolute -z-[1] left-0 top-0 h-full w-full overflow-hidden rounded-xl">
              <div className="absolute left-0 top-0 h-full w-full">
                <Image
                  height={600}
                  width={420}
                  className={cn(
                    "rounded-xl brightness-[0.7] transition-all duration-300 gradient-mask-b-60",
                    transformRotate,
                    transformScale,
                  )}
                  alt={deck.name}
                  src={deck.feature.src}
                />
              </div>
              <div
                className="absolute h-full w-full"
                style={{
                  backgroundImage: `
          linear-gradient(240deg, rgba(2, 2, 2, 0.8) 0%, rgba(2, 2, 2, 0.1) 30%),
          linear-gradient(120deg, rgba(2, 2, 2, 0.8) 0%, rgba(2, 2, 2, 0.1) 30%),
          linear-gradient(180deg, rgba(2, 2, 2, 0.3) 0%, rgba(2, 2, 2, 0.1) 40%)`,
                }}
              />
            </div>

            {/* Card */}
            <div className="h-full p-4 !pb-3">
              <div className="flex h-full flex-col justify-end">
                <div className="flex justify-between text-sm">
                  {/* badges */}
                  <div className="flex">
                    {isNew(deck.createdAt) && (
                      <div className="whitespace-nowrap pr-2">
                        <GiCometSpark size={11} className="-mt-1 mr-1 inline text-white" />
                        <span className="text-white">New</span>
                      </div>
                    )}

                    {hasPrimer(deck.primer) && (
                      <div className="whitespace-nowrap pr-2">
                        <GiCrossedSwords size={11} className="text-red -mt-0.5 mr-0.5 inline" />

                        <span className="text-red">Primer</span>
                      </div>
                    )}

                    {isPrivate(deck.visibility) && (
                      <div className="whitespace-nowrap pr-2">
                        <BsEyeSlashFill size={11} className="text-info -mt-[3px] mr-0.5 inline" />

                        <span className="text-info">Private</span>
                      </div>
                    )}
                  </div>

                  {/* affinity */}
                  <DeckThreshholds elements={deck.elements} />
                </div>

                <div className="flex flex-row items-center justify-between">
                  <div className="mb-0 mr-2 truncate text-xl font-bold text-white">{deck.name}</div>
                </div>

                <div className="flex justify-between text-sm">
                  <div className="truncate text-sm opacity-80">{fromNow(deck.updatedAt)}</div>
                  <Metadata
                    views={deck._count.views}
                    comments={deck._count.comments}
                    likes={deck._count.likes}
                    className="opacity-80"
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          // list view

          <div className="flex items-center">
            <div className="relative h-[40px] w-[60px] overflow-hidden mr-4 rounded-md">
              <div className="absolute left-0 top-0 h-full w-full">
                <Image
                  height={600}
                  width={420}
                  className={cn("brightness-110 transition-all", transformRotate)}
                  alt={deck.name}
                  src={deck.feature.src}
                />
              </div>

              <div
                className="absolute h-full w-full"
                style={{
                  backgroundImage: `
          linear-gradient(270deg, rgba(2, 2, 2, 0.7) 0%, rgba(2, 2, 2, 0.05) 20%),
          linear-gradient(90deg, rgba(2, 2, 2, 0.7) 0%, rgba(2, 2, 2, 0.05) 20%),
          linear-gradient(180deg, rgba(2, 2, 2, 0.7) 0%, rgba(2, 2, 2, 0.05) 20%),
          linear-gradient(0deg, rgba(2, 2, 2, 0.7) 0%, rgba(2, 2, 2, 0.05) 20%)`,
                }}
              />
            </div>

            {/* left */}
            <div className="mr-2 grow flex flex-col">
              <div className="flex">
                {/* star */}
                {starred && <IoStar className="text-lg mr-2" />}

                <div className="mr-2 max-w-[9rem] sm:max-w-[20rem] md:max-w-[28rem] lg:max-w-[36rem] xl:max-w-[50rem] truncate">
                  {deck.name}
                </div>
              </div>

              <div className="flex">
                <div className="opacity-70 mr-3 italic">{deck.avatars[0]?.card.name}</div>

                {/* affinity */}
                {!!deck.elements.length && (
                  <>
                    <span className="text-secondary pr-2">|</span>
                    <div className="-mt-0.5 pr-2">
                      <DeckThreshholds elements={deck.elements} />
                    </div>
                  </>
                )}

                {(isNew(deck.createdAt) ||
                  hasPrimer(deck.primer) ||
                  isPrivate(deck.visibility)) && (
                  <div className="hidden sm:flex">
                    {/* badges */}
                    <span className="text-secondary pr-2">|</span>
                    {isNew(deck.createdAt) && (
                      <div className="whitespace-nowrap pr-2 mr-2">
                        <GiCometSpark size={11} className="-mt-1 mr-1 inline text-white" />

                        <span className="text-white">New</span>
                      </div>
                    )}

                    {hasPrimer(deck.primer) && (
                      <div className="whitespace-nowrap pr-2">
                        <GiCrossedSwords size={11} className="text-red -mt-0.5 mr-0.5 inline" />

                        <span className="text-red">Primer</span>
                      </div>
                    )}

                    {isPrivate(deck.visibility) && (
                      <div className="whitespace-nowrap pr-1">
                        <BsEyeSlashFill size={11} className="text-info -mt-[3px] mr-0.5 inline" />

                        <span className="text-info">Private</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </InternalLink>

      {/* star */}
      {layout === LayoutEnum.GRID && (
        <div className={"absolute left-0 top-0 z-10"}>
          <Button
            variant={"minimal"}
            className={cn(
              "transition-opacity p-2",
              starred ? "opacity-100" : "sm:opacity-0 group-hover:opacity-100",
              isStarring && "opacity-60",
            )}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleStar({ id: deck.id });
            }}
            disabled={isStarring}
          >
            {starred ? (
              <IoStar className="text-2xl transition-all duration-200 hover:scale-125" />
            ) : (
              <IoStarOutline className="text-2xl transition-all duration-200 hover:scale-125" />
            )}
          </Button>
        </div>
      )}

      <div
        className={cn(
          layout === LayoutEnum.GRID &&
            "absolute right-0 top-0 z-10 p-1 sm:opacity-0 group-hover:opacity-100 flex",
          layout === LayoutEnum.LIST && "mx-1 flex items-center",
        )}
      >
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild className="pr-2 hover:opacity-60">
            <Button variant="minimal">
              <MdOutlineMoreHoriz size={18} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="bottom" align="end">
            <DropdownMenuItem onClick={() => toggleStar({ id: deck.id })}>
              {starred ? "Unstar" : "Star"}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                updateSettings({
                  deckId: deck.id,
                  visibility:
                    deck.visibility === DeckVisibilityEnum.Private
                      ? DeckVisibilityEnum.Public
                      : DeckVisibilityEnum.Private,
                })
              }
            >
              {deck.visibility === DeckVisibilityEnum.Private ? "Make Public" : "Make Private"}
            </DropdownMenuItem>
            {(!!folders?.length || folderId) && (
              <DropdownMenuItem
                onClick={() =>
                  openModal(ModalTypeEnum.MOVEDECKTOFOLDER, {
                    deckId: deck.id,
                    deckName: deck.name,
                  })
                }
              >
                Move
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onDelete} className="hover:!bg-red hover:!text-white">
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {(!!folders?.length || folderId) && (
          <Button
            variant={"minimal"}
            {...attributes}
            {...listeners}
            className={cn(
              "hidden sm:block px-1 cursor-grab hover:opacity-60",
              isDragging && "!cursor-[grabbing]",
            )}
          >
            <RxDragHandleDots2 size={18} color={"white"} />
          </Button>
        )}
      </div>
    </div>
  );
};
